const TEXT_ENTRY_SELECTOR = [
  'input:not([type])',
  'input[type="text"]',
  'input[type="search"]',
  'input[type="email"]',
  'input[type="url"]',
  'input[type="tel"]',
  'input[type="password"]',
  'input[type="number"]',
  'input[type="date"]',
  'input[type="time"]',
  'input[type="datetime-local"]',
  'input[type="month"]',
  'input[type="week"]',
  'textarea',
  '[contenteditable="true"]',
].join(', ');

const TABLET_BOTTOM_SHEET_MAX_WIDTH = 1366;
const TOUCH_BOTTOM_SHEET_MAX_WIDTH = 1600;

export function isTextEntryField(field) {
  return Boolean(field?.matches?.(TEXT_ENTRY_SELECTOR));
}

export function shouldUseBottomSheetViewport({ width, touchPoints = 0, coarsePointer = false } = {}) {
  const viewportWidth = Number(width) || 0;
  if (viewportWidth <= 0) return false;
  return viewportWidth <= TABLET_BOTTOM_SHEET_MAX_WIDTH
    || ((Number(touchPoints) > 0 || coarsePointer) && viewportWidth <= TOUCH_BOTTOM_SHEET_MAX_WIDTH);
}

function getTouchCapabilities() {
  if (typeof window === 'undefined') return { touchPoints: 0, coarsePointer: false };
  return {
    touchPoints: Number(window.navigator?.maxTouchPoints || 0),
    coarsePointer: Boolean(
      window.matchMedia?.('(pointer: coarse)')?.matches
      || window.matchMedia?.('(any-pointer: coarse)')?.matches
    ),
  };
}

export function isBottomSheetViewport() {
  if (typeof window === 'undefined') return true;
  const { touchPoints, coarsePointer } = getTouchCapabilities();
  return shouldUseBottomSheetViewport({
    width: window.visualViewport?.width || window.innerWidth,
    touchPoints,
    coarsePointer,
  });
}

export function shouldCenterFocusedField(viewport) {
  return isBottomSheetViewport() || Number(viewport?.bottomInset) > 80;
}

export function getFocusScrollDelta(fieldRect, visibleTop, visibleBottom, center = false) {
  if (!fieldRect || visibleBottom <= visibleTop) return 0;
  if (!center && fieldRect.top >= visibleTop && fieldRect.bottom <= visibleBottom) return 0;

  const visibleHeight = visibleBottom - visibleTop;
  const fieldHeight = Math.max(0, fieldRect.height || fieldRect.bottom - fieldRect.top);
  const targetTop = fieldHeight <= visibleHeight
    ? visibleTop + (visibleHeight - fieldHeight) / 2
    : visibleTop;

  return fieldRect.top - targetTop;
}

function canScrollVertically(element) {
  if (!element || element.clientHeight <= 0 || element.scrollHeight <= element.clientHeight + 1) return false;
  const overflowY = window.getComputedStyle(element).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
}

/** Reveal an element by scrolling only the supplied container, never the page or its other ancestors. */
export function scrollElementWithinContainer(container, element, { behavior = 'smooth', margin = 12 } = {}) {
  if (!container || !element || !container.contains?.(element) || !canScrollVertically(container)) return false;

  const containerRect = container.getBoundingClientRect();
  const visibleTop = containerRect.top + container.clientTop + margin;
  const visibleBottom = containerRect.top + container.clientTop + container.clientHeight - margin;
  if (visibleBottom <= visibleTop) return false;

  const elementRect = element.getBoundingClientRect();
  const delta = elementRect.top < visibleTop
    ? elementRect.top - visibleTop
    : elementRect.bottom > visibleBottom
      ? elementRect.bottom - visibleBottom
      : 0;
  if (Math.abs(delta) <= 1) return false;

  const top = Math.max(0, container.scrollTop + delta);
  if (typeof container.scrollTo === 'function') container.scrollTo({ top, behavior });
  else container.scrollTop = top;
  return true;
}

/** Keep a focused form field visible, scrolling nested panels before their parent. */
export function keepElementVisible({ root, element, viewport, margin = 12, center = false }) {
  if (!element || (root && !root.contains(element))) return false;
  const viewportTop = Math.max(0, Number(viewport?.top) || 0) + margin;
  const viewportBottom = (Number(viewport?.top) || 0) + (Number(viewport?.height) || window.innerHeight) - margin;
  if (viewportBottom <= viewportTop) return false;
  const scrollContainers = [];

  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (canScrollVertically(ancestor)) scrollContainers.push(ancestor);
    if (ancestor === root) break;
  }
  if (root && !scrollContainers.includes(root) && canScrollVertically(root)) scrollContainers.push(root);

  let adjusted = false;
  // Center within the nearest scroller (for example, the lot list). Ancestors
  // move only as much as needed to keep that field inside the visible area.
  for (let pass = 0; pass < scrollContainers.length; pass += 1) {
    let adjustedThisPass = false;
    scrollContainers.forEach((container, index) => {
      const rect = container.getBoundingClientRect();
      const contentTop = rect.top + container.clientTop;
      const contentBottom = contentTop + container.clientHeight;
      const visibleTop = Math.max(viewportTop, contentTop + 4);
      const visibleBottom = Math.min(viewportBottom, contentBottom - 4);
      const centerThisContainer = center && index === 0;
      const delta = getFocusScrollDelta(element.getBoundingClientRect(), visibleTop, visibleBottom, centerThisContainer);
      if (Math.abs(delta) > 1) {
        container.scrollTop += delta;
        adjusted = true;
        adjustedThisPass = true;
      }
    });
    if (!adjustedThisPass) break;
  }

  return adjusted;
}

export function keepFocusedFieldVisible({ root, field = document.activeElement, viewport, margin = 12, center = false }) {
  if (!root || !field || !root.contains(field) || !isTextEntryField(field)) return false;
  return keepElementVisible({ root, element: field, viewport, margin, center });
}
